import * as React from "react";
import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import RecursosHumanos from "./permisos/RH";
import Adminis from "./permisos/Admin";
import Lavadora from "./permisos/Lavadora";
import SalaLimpia from "./permisos/SalaLimpia";
import Interfoliacion from "./permisos/Interfoliacion";
import Calandra from "./permisos/Calandra";
import Stock from "./permisos/Stock";
import Supervisor from "./permisos/Supervisor";
import Mantenimiento from "./permisos/Mantenimiento";
import RecursosHumanosA from "./permisos/AuxRh";
import PuenteGrua from "./permisos/PuenteGrua";
import Directivos from "./permisos/Directivos";
import Laboratorio from "./permisos/Laboratorio";
import Contabilidad from "./permisos/Contabilidad";
import Venta from "./permisos/Venta";
import Tejido from "./permisos/Tejido";
import TejidoVendedor from "./permisos/TejidoVendedor";
import Deposito from "./permisos/Deposito";
import VidrioGranular from "./permisos/VidrioGranular";
import DF from "./permisos/DF";
import {
  normalizePermissionKeys,
  parsePermissionOverrides,
  resolveSidebarModuleKeys,
} from "../../utils/permissions";
import secureStorage from "../../utils/secureStorage";


const PERMISSION_COMPONENTS = {
  ADMIN: Adminis,
  SUPERVISOR: Supervisor,
  CONTABILIDAD: Contabilidad,
  RH: RecursosHumanos,
  CALANDRA: Calandra,
  STOCK: Stock,
  LAVADORA: Lavadora,
  SALA_LIMPIA: SalaLimpia,
  INTERFOLIACION: Interfoliacion,
  MANTENIMIENTO: Mantenimiento,
  AUX_RH: RecursosHumanosA,
  PUENTE_GRUA: PuenteGrua,
  DIRECTIVOS: Directivos,
  LABORATORIO: Laboratorio,
  VENTA: Venta,
  TEJIDO: Tejido,
  TEJIDO_VENDEDOR: TejidoVendedor,
  DEPOSITO: Deposito,
  VIDRIO: VidrioGranular,
  DF: DF,
};

const LEVEL_PERMISSION_KEYS = {
  1: ["ADMIN"],
  2: ["SUPERVISOR"],
  4: ["RH"],
  5: ["CALANDRA"],
  6: ["STOCK"],
  7: ["LAVADORA"],
  8: ["SALA_LIMPIA"],
  9: ["INTERFOLIACION"],
  10: ["MANTENIMIENTO"],
  11: ["AUX_RH"],
  12: ["PUENTE_GRUA"],
  13: ["CONTABILIDAD"],
  15: ["DIRECTIVOS"],
  16: ["LABORATORIO"],
  17: ["VENTA"],
  18: ["TEJIDO"],
  19: ["TEJIDO_VENDEDOR"],
  20: ["DEPOSITO"],
  21: ["DF"],
};


const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};
// img.src = "../../assets/zd.jpg";
const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");
  const [userLevel, setUserLevel] = useState(null);
  const [user, setUser] = useState(null);
  const [effectivePermissionKeys, setEffectivePermissionKeys] = useState([]);
  const [extraPermissionKeys, setExtraPermissionKeys] = useState([]);
  const [blockedPermissionKeys, setBlockedPermissionKeys] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(() => !isMobile);
  const isDracenaRoute = location.pathname === "/";
  const sidebarBackground = isDracenaRoute ? "rgba(8, 20, 40, 0.55)" : colors.primary[400];
  const sidebarBorder = isDracenaRoute ? "rgba(248, 210, 122, 0.35)" : "rgb(61, 66, 84)";
  const sidebarHoverColor = isDracenaRoute ? "#f8d27a" : colors.primary[100];
  const sidebarActiveBackground = isDracenaRoute ? "rgba(255, 255, 255, 0.12)" : colors.primary[450];

  useEffect(() => {
    const level = secureStorage.getItem("userLevel");
    const user = secureStorage.getItem("user");
    const permissionOverrides = parsePermissionOverrides(
      secureStorage.getItem("userPermissions")
    );

    const extraPermissions = normalizePermissionKeys(
      secureStorage.getItem("userExtraPermissions")
    );
    const effectivePermissions = normalizePermissionKeys(
      secureStorage.getItem("userEffectivePermissions")
    );
    const blockedPermissions = normalizePermissionKeys(
      secureStorage.getItem("userBlockedPermissions")
    );

    if (level) {
      setUserLevel(parseInt(level));
    }
    if (user) {
      setUser(user.toUpperCase());
    }
    setExtraPermissionKeys([
      ...permissionOverrides.add,
      ...extraPermissions,
    ]);
    setEffectivePermissionKeys(effectivePermissions);
    setBlockedPermissionKeys([
      ...permissionOverrides.remove,
      ...blockedPermissions,
    ]);
  }, []);

  const resolvedPermissionKeys = React.useMemo(() => {
    const merged = resolveSidebarModuleKeys({
      userLevel,
      levelPermissionKeys: LEVEL_PERMISSION_KEYS,
      effectivePermissionKeys,
      extraPermissionKeys,
      blockedPermissionKeys,
      allowedModuleKeys: Object.keys(PERMISSION_COMPONENTS),
    });

    return merged.filter(
      (permissionKey) => PERMISSION_COMPONENTS[permissionKey]
    );
  }, [userLevel, effectivePermissionKeys, extraPermissionKeys, blockedPermissionKeys]);

  const componentPermissionKeys = React.useMemo(() => {
    return [
      ...effectivePermissionKeys,
      ...extraPermissionKeys,
    ];
  }, [effectivePermissionKeys, extraPermissionKeys]);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false);
      setMobileOpen(false);
    } else {
      setMobileOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => undefined;
    }
    const handleToggle = () => {
      if (isMobile) {
        setMobileOpen((prev) => !prev);
      } else {
        setIsCollapsed((prev) => !prev);
      }
    };
    const handleClose = () => {
      if (isMobile) {
        setMobileOpen(false);
      } else {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("toggleSidebar", handleToggle);
    window.addEventListener("closeSidebar", handleClose);
    return () => {
      window.removeEventListener("toggleSidebar", handleToggle);
      window.removeEventListener("closeSidebar", handleClose);
    };
  }, [isMobile]);

  const handleMenuToggle = () => {
    if (isMobile) {
      setMobileOpen(false);
      return;
    }
    setIsCollapsed((prev) => !prev);
  };

  const logoSrc =
    theme.palette.mode === "dark"
      ? "../../assets/dracenaS.png"
      : "../../assets/dracenaC.png";

  const handleLogoClick = () => {
    navigate("/");
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <Box
          onClick={() => setMobileOpen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            zIndex: 2050,
          }}
        />
      )}
      <Box
        sx={{
          position: isMobile ? "fixed" : "relative",
          top: 0,
          left: 0,
          height: isMobile ? "100vh" : "auto",
          width: isMobile ? "270px" : "auto",
          maxWidth: isMobile ? "80vw" : "none",
          zIndex: isMobile ? 2100 : "auto",
          transform: isMobile
            ? mobileOpen
              ? "translateX(0)"
              : "translateX(-110%)"
            : "none",
          transition: "transform 0.3s ease",
          pointerEvents: isMobile ? (mobileOpen ? "auto" : "none") : "auto",
          boxShadow:
            isMobile && mobileOpen
              ? "0 20px 45px rgba(0, 0, 0, 0.45)"
              : "none",
          backgroundColor: isMobile ? sidebarBackground : "transparent",
          backdropFilter: isDracenaRoute ? "blur(6px)" : "none",
          "& .pro-sidebar": {
            height: "100vh",
          },
          "& .pro-sidebar-inner": {
            background: `${sidebarBackground} !important`,
            borderRight: `1px solid ${sidebarBorder}`,
            backdropFilter: isDracenaRoute ? "blur(6px)" : "none",
          },
          "& .pro-icon-wrapper": {
            backgroundColor: "transparent !important",
          },
          "& .pro-inner-item": {
            padding: "5px 35px 5px 20px !important",
          },
          "& .pro-inner-item:hover": {
            fontSize: "16px",
            borderLeft: "5px solid rgb(206, 220, 0)",
            color: `${sidebarHoverColor} !important`,
          },
          "& .pro-menu-item.active": {
            color: `${sidebarHoverColor} !important`,
            fontSize: "16px",
            background: `${sidebarActiveBackground} !important`,
            borderLeft: "5px solid rgb(206, 220, 0)",
            backdropFilter: isDracenaRoute ? "blur(6px)" : "none",
          },
          "& .css-17w9904-MuiTypography-root ": {
            fontSize: "16px",
          },
          " & .css-1musjwt .pro-inner-item": {
            padding: "0 !important",
          },
          "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu .pro-inner-list-item .pro-inner-item":
            {
              padding: "8px 30px 8px 5px !important",
            },
        }}
      >
        <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={handleMenuToggle}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[300],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h3" color={colors.grey[300]}>
                  <IconButton sx={{ marginTop: "-5%" }}>
                    <img
                      src="../../assets/boton.png"
                      alt=""
                      style={{
                        width: "20px",
                      }}
                    />
                  </IconButton>
                  {user}
                </Typography>
                <IconButton onClick={handleMenuToggle}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="5px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="250px"
                  height="90px"
                  src={logoSrc}
                  style={{ cursor: "pointer", borderRadius: "0%" }}
                  onClick={handleLogoClick}
                />
              </Box>
              <Box textAlign="center"></Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "1%"}>
            {resolvedPermissionKeys.map((permissionKey) => {
              const PermissionComponent = PERMISSION_COMPONENTS[permissionKey];
              return (
                <PermissionComponent
                  key={permissionKey}
                  selected={selected}
                  setSelected={setSelected}
                  permissionKeys={componentPermissionKeys}
                />
              );
            })}
          </Box>
        </Menu>
      </ProSidebar>
      </Box>
    </>
  );
};

export default Sidebar;
